import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    XMarkIcon,
    ChevronRightIcon,
    ClipboardIcon,
    CheckIcon,
    EllipsisHorizontalIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from '@odo/shared-ui';
import {
    type WorkQueueCase,
    type StatusMeta,
    type OutgoingAction,
    type WorkflowMeta
} from '../../services/workQueue';
import { useStatusCatalog, type StatusCatalog } from '../../services/statusCatalog';
import { routeForAction } from '../../services/workflowActionRoutes';
import { fetchTimeline, fetchJourney } from '../../services/timeline';
import { fetchPatientCard } from '../../services/patientCard';
import { ForceStatusModal } from './ForceStatusModal';
import { ExitHoldConfirmModal } from './ExitHoldConfirmModal';
import { statusPreview, type StatusPreview } from './statusPreview';
import { PutOnHoldModal } from './PutOnHoldModal';
import type { TimelineEvent } from '../patient-files/actions/types';
import { TimelineRailEvent } from '../patient-files/timeline-rail/TimelineRailEvent';
import { JourneyView } from '../patient-files/timeline-rail/JourneyView';
import {
    TimelineModeToggle,
    type TimelineMode
} from '../patient-files/timeline-rail/TimelineModeToggle';
import AssigneeSelector from '../conversations/AssigneeSelector';
import { Avatar } from '../Avatar';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import { useCaseAssignment } from '../../hooks/useCaseAssignment';
import { AgingBadge } from './AgingBadge';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { AGING_EXEMPT_STATUS_IDS, NOTES_REQUIRED_ACTIONS } from './constants';
import { PHASE_HEX } from '../../utils/phaseColors';
import { ManufacturerStatusPill } from './ManufacturerStatusPill';
import { CopyPatientNameButton } from './CopyPatientNameButton';
import { PrioritySelector } from './priority/PrioritySelector';
import { accountFor, accountOwnerLabel } from '../../utils/manufacturer';
import { copyToClipboard } from '../../utils/clipboard';

const TIMELINE_STALE_MS = 60_000;
const TIMELINE_PAGE_LIMIT = 50;

// Mirrors TimelineRail's sortNewestFirst; unregistered event types render
// with the shared fallback visual instead of being hidden.
function sortNewestFirst(events: TimelineEvent[]): TimelineEvent[] {
    return [...events].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
}

const MS_PER_DAY = 86_400_000;
function ageDaysFromIso(iso: string): number {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return 0;
    return Math.max(0, Math.floor((Date.now() - ts) / MS_PER_DAY));
}

interface DetailPanelProps {
    workCase: WorkQueueCase;
    statusMeta: StatusMeta | undefined;
    meta: WorkflowMeta | undefined;
    onClose: () => void;
    onAction: (c: WorkQueueCase, outgoingAction: OutgoingAction) => void;
    onStartTask: (id: string) => void;
}

// archive-case has no graph edge (it's a GlobalAction, fired only from the
// patient-files Actions tab) - filtered here as a defensive guard regardless.
function internalActions(statusMeta: StatusMeta | undefined): OutgoingAction[] {
    if (!statusMeta) return [];
    // 'both' counts as internal: reactivate-case, shared with the practice, is the only edge
    // on terminal statuses, so filtering it out would leave no ODO-facing action.
    return statusMeta.outgoingActions.filter(
        (a) =>
            (a.actor === 'internal' || a.actor === 'both') &&
            a.action !== 'archive-case' &&
            !NOTES_REQUIRED_ACTIONS.has(a.action)
    );
}

// Resolve the resume destination for an exit-hold action as a preview chip.
// Undefined resumeFromStatus (legacy hold) falls through to the modal's generic copy.
function exitHoldTargetPreview(
    workCase: WorkQueueCase,
    catalog: StatusCatalog
): StatusPreview | undefined {
    const resume = workCase.resumeFromStatus;
    if (resume == null) return undefined;
    return statusPreview(resume, catalog);
}

export function DetailPanel({
    workCase: c,
    statusMeta,
    meta,
    onClose,
    onAction,
    onStartTask
}: DetailPanelProps) {
    const navigate = useNavigate();
    // Full queue URL (filters + ?case=) at the moment we leave - the patient
    // page's breadcrumb restores it exactly.
    const location = useLocation();
    const catalog = useStatusCatalog();
    const phaseColor = PHASE_HEX[c.phase] ?? 'rgb(107,114,128)';
    const actions = internalActions(statusMeta);
    const task = statusMeta?.task ?? null;
    // "Put on hold" is a global action, available from any non-hold/terminal
    // status, rendered once at the top of "Next action" regardless of task/actions.
    const canPutOnHold =
        Boolean(statusMeta) && statusMeta?.macro !== 'hold' && statusMeta?.macro !== 'terminal';
    const canExitHold =
        statusMeta?.macro === 'hold' &&
        statusMeta.outgoingActions.some((a) => a.action === 'exit-hold');
    const { roles, currentUserEmail, handleAssign, handleUnassign } = useCaseAssignment(
        c,
        'DetailPanel'
    );
    const [overlayOpen, setOverlayOpen] = useState(false);
    // The `…` menu dismisses on outside click and Escape, like every other
    // popover (see PriorityMenu / FilterDropdown for the same pattern).
    const overlayRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!overlayOpen) return;
        const handleMouseDown = (e: MouseEvent) => {
            if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
                setOverlayOpen(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOverlayOpen(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [overlayOpen]);
    const [forceOpen, setForceOpen] = useState(false);
    const [exitHoldOpen, setExitHoldOpen] = useState(false);
    const [putOnHoldOpen, setPutOnHoldOpen] = useState(false);

    // Unlike useCases/useGroups, these detail queries don't poll - refetch on
    // window focus so a case that changed while the panel sat open is current.
    const {
        data: timelineData,
        isLoading: timelineLoading,
        isError: timelineError
    } = useQuery({
        queryKey: ['patient-timeline', c.id],
        queryFn: () => fetchTimeline(c.id, { isLatest: true, limit: TIMELINE_PAGE_LIMIT }),
        staleTime: TIMELINE_STALE_MS,
        enabled: Boolean(c.id),
        refetchOnWindowFocus: true
    });

    // Journey mode (steps/phases + durations) is fetched lazily on first toggle.
    const [timelineMode, setTimelineMode] = useState<TimelineMode>('activity');
    const journeyQuery = useQuery({
        queryKey: ['patient-journey', c.id],
        queryFn: () => fetchJourney(c.id),
        staleTime: TIMELINE_STALE_MS,
        enabled: Boolean(c.id) && timelineMode === 'journey',
        refetchOnWindowFocus: true
    });

    const { data: card } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: TIMELINE_STALE_MS,
        enabled: Boolean(c.id),
        refetchOnWindowFocus: true
    });

    // A practice can hold both a Spark and an Invisalign account, so copy the one
    // matching this case's manufacturer — the other would be the wrong portal.
    const manufacturer = card?.treatment?.manufacturerSlug ?? null;
    const manufacturerAcct = accountFor(card?.practice, manufacturer);
    const acctLabel = accountOwnerLabel(manufacturer);
    const [acctCopied, setAcctCopied] = useState(false);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
        },
        []
    );
    const handleCopyManufacturerAcct = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!manufacturerAcct) return;
        if (!(await copyToClipboard(manufacturerAcct))) return;
        setAcctCopied(true);
        copyTimerRef.current = setTimeout(() => setAcctCopied(false), 1000);
    };

    const timelineEvents = React.useMemo(
        () => sortNewestFirst(timelineData?.data ?? []),
        [timelineData]
    );

    return (
        <div className="h-full w-full flex flex-col bg-white overflow-hidden">
            {/* Header - mirrors the Conversations sliding panel: avatar + clickable
          name (link to patient page) + practice with copy-spark-acct affordance. */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0">
                        <Avatar
                            user={{ name: c.name, email: '' }}
                            imageURL={c.profilePictureUrl ?? undefined}
                            thumbnailURL={c.profileThumbnailUrl ?? undefined}
                        />
                    </div>
                    <div className="min-w-0 text-left">
                        <div className="group/name flex items-center gap-1 flex-wrap">
                            <a
                                href={`/patients/${c.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 group focus:outline-none"
                            >
                                <span className="text-sm font-bold text-gray-900 truncate group-hover:underline leading-tight">
                                    {c.name}
                                </span>
                                <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                            </a>
                            <CopyPatientNameButton name={c.name} />
                        </div>
                        {c.practice && (
                            <div className="inline-flex items-center gap-1 leading-tight">
                                <button
                                    type="button"
                                    disabled={!manufacturerAcct}
                                    onClick={
                                        manufacturerAcct ? handleCopyManufacturerAcct : undefined
                                    }
                                    className={
                                        manufacturerAcct
                                            ? 'text-gray-400 hover:text-gray-600 transition-colors'
                                            : 'text-gray-300 cursor-not-allowed'
                                    }
                                    aria-label={
                                        manufacturerAcct
                                            ? `Copy ${acctLabel} account`
                                            : `No ${acctLabel} account`
                                    }
                                >
                                    {acctCopied ? (
                                        <CheckIcon className="w-3.5 h-3.5" />
                                    ) : (
                                        <ClipboardIcon className="w-3.5 h-3.5" />
                                    )}
                                </button>
                                <PracticeDoctorLabel
                                    practiceName={c.practice}
                                    practiceId={card?.practice?.id}
                                    format="practice-only"
                                    className="text-xs truncate"
                                    toneClassName="text-gray-500"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div ref={overlayRef} className="flex items-center gap-1 flex-shrink-0 relative">
                    {statusMeta?.macro !== 'hold' && (
                        <>
                            <button
                                onClick={() => setOverlayOpen(!overlayOpen)}
                                className={`p-1 rounded-md transition-colors ${
                                    overlayOpen
                                        ? 'bg-gray-100 text-gray-700'
                                        : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                                aria-label="More actions"
                                aria-expanded={overlayOpen}
                            >
                                <EllipsisHorizontalIcon className="w-5 h-5" />
                            </button>
                            {overlayOpen && (
                                // Mirrors the PriorityMenu popover chrome (rounded-[10px], soft
                                // long shadow, uppercase section header, title + description rows).
                                <div
                                    className="absolute top-full right-0 mt-1 z-50 w-[200px] bg-white border border-gray-200 rounded-[10px] shadow-[0_10px_28px_rgba(0,0,0,0.13)] py-1"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() => {
                                            setOverlayOpen(false);
                                            setForceOpen(true);
                                        }}
                                        className="w-full flex items-center px-3 py-1.5 gap-2.5 hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-amber-50">
                                            <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600" />
                                        </span>
                                        <span className="flex-1 text-left text-[13px] font-semibold text-gray-800">
                                            Force status{' '}
                                            <span className="font-normal text-gray-400">
                                                (admin)
                                            </span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close detail panel"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {forceOpen && (
                <ForceStatusModal
                    workCase={c}
                    meta={meta}
                    practiceId={card?.practice?.id}
                    onClose={() => setForceOpen(false)}
                />
            )}

            {putOnHoldOpen && (
                <PutOnHoldModal workCase={c} onClose={() => setPutOnHoldOpen(false)} />
            )}

            {exitHoldOpen && (
                <ExitHoldConfirmModal
                    workCase={c}
                    from={{
                        phase: statusMeta?.phase ?? c.phase,
                        name: catalog.display(c.statusId)
                    }}
                    to={exitHoldTargetPreview(c, catalog)}
                    onClose={() => setExitHoldOpen(false)}
                />
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-3.5">
                {/* Status strip */}
                <div
                    className="bg-gray-50 rounded-lg p-3 mb-3.5"
                    style={{ borderLeft: `3px solid ${phaseColor}` }}
                >
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.04em] mb-1">
                        {c.phase} phase
                    </div>
                    <div className="text-[13px] text-gray-800 font-medium mb-2">
                        {catalog.display(c.statusId)}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <TeamPill team={c.team} size="xs" />
                        <AgingBadge
                            days={ageDaysFromIso(c.stepEnteredAt ?? c.updatedAt)}
                            neutral={AGING_EXEMPT_STATUS_IDS.has(c.statusId)}
                        />
                    </div>
                </div>

                {/* Fields — assignee + priority share one block, bordered above and below */}
                <div className="border-y border-gray-100">
                    <div className="flex justify-between items-center py-2.5 gap-2">
                        <span className="text-[12px] font-medium text-gray-500">Assignee</span>
                        <AssigneeSelector
                            assignedTo={c.assignee}
                            roles={roles}
                            onAssign={handleAssign}
                            onUnassign={handleUnassign}
                            currentUserEmail={currentUserEmail}
                        />
                    </div>

                    <div className="flex justify-between items-center py-2.5 gap-2">
                        <span className="text-[12px] font-medium text-gray-500">Priority</span>
                        <PrioritySelector patientId={c.id} priority={c.priority} align="right" />
                    </div>

                    {/* Manufacturer — read-only context (the aligner manufacturer, e.g.
              "Spark Aligners"). Mirrors the work-queue MANUFACTURER column so
              the portal is identifiable without leaving the panel. Hidden when
              no manufacturer is on file. */}
                    {c.manufacturer && (
                        <div className="flex justify-between items-center py-2.5 gap-2">
                            <span className="text-[12px] font-medium text-gray-500 flex-shrink-0">
                                Manufacturer
                            </span>
                            <Tooltip content={c.manufacturer} side="top">
                                <span className="text-[12.5px] text-gray-800 truncate min-w-0">
                                    {c.manufacturer}
                                </span>
                            </Tooltip>
                        </div>
                    )}

                    {/* Manufacturer status — read-only context: the case status the
              manufacturer reports (patient_monday_data.manufacturer_case_status,
              synced from the portal). Mirrors the work-queue MANUFACTURER
              STATUS column. Hidden when nothing is synced. */}
                    {c.manufacturerCaseStatus && (
                        <div className="flex justify-between items-center py-2.5 gap-2">
                            <span className="text-[12px] font-medium text-gray-500 flex-shrink-0">
                                Manufacturer status
                            </span>
                            <Tooltip content={c.manufacturerCaseStatus} side="top">
                                <span className="min-w-0">
                                    <ManufacturerStatusPill status={c.manufacturerCaseStatus} />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>

                {/* Next action */}
                <div className="mt-3.5 bg-gray-50 rounded-lg p-3">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.04em] mb-1.5">
                        Next action
                    </div>

                    <div className="flex flex-col gap-1.5">
                        {statusMeta?.deprecated ? (
                            <>
                                <p className="text-[12px] text-gray-600">
                                    This step is retired. Use Force status to move the case to its
                                    current step.
                                </p>
                                <button
                                    onClick={() => setForceOpen(true)}
                                    className="w-full py-2 rounded-md bg-gray-800 text-white text-xs font-bold hover:bg-gray-900 transition-colors"
                                >
                                    Force status
                                </button>
                            </>
                        ) : task ? (
                            <button
                                onClick={() => onStartTask(c.id)}
                                className="w-full px-3 py-2 rounded-md border border-[rgb(55,115,168)] text-[rgb(55,115,168)] text-xs font-bold bg-white hover:bg-[rgb(55,115,168)] hover:text-white transition-colors"
                            >
                                {task.label}
                            </button>
                        ) : actions.length === 0 ? (
                            <p className="text-[12px] text-gray-400 italic">Waiting on practice</p>
                        ) : (
                            actions.map((a) => {
                                const wired = routeForAction(a.action) !== null;
                                const baseClass =
                                    a.direction === 'backward'
                                        ? 'w-full py-2 rounded-md border border-red-500 text-red-500 text-xs font-bold bg-white hover:bg-red-50 transition-colors'
                                        : 'w-full py-2 rounded-md bg-gray-800 text-white text-xs font-bold hover:bg-gray-900 transition-colors';
                                const btn = (
                                    <button
                                        onClick={() => onAction(c, a)}
                                        disabled={!wired}
                                        className={`${baseClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                                    >
                                        {a.label}
                                    </button>
                                );
                                return wired ? (
                                    <div key={a.action}>{btn}</div>
                                ) : (
                                    <Tooltip
                                        key={a.action}
                                        content="Action route not implemented yet"
                                        side="top"
                                    >
                                        <span className="block">{btn}</span>
                                    </Tooltip>
                                );
                            })
                        )}
                    </div>

                    <div className="mt-2.5 pt-2.5 border-t border-gray-200 flex flex-col gap-1.5">
                        {/* Exit hold — prominent path for hold cases; replaces the overflow menu item */}
                        {canExitHold && (
                            <button
                                onClick={() => setExitHoldOpen(true)}
                                className="w-full py-2 rounded-md border border-orange-400 text-orange-700 text-xs font-bold bg-white hover:bg-orange-50 transition-colors"
                            >
                                Exit hold
                            </button>
                        )}
                        {/* Put on hold — global action, grouped above "Other actions" */}
                        {canPutOnHold && (
                            <button
                                onClick={() => setPutOnHoldOpen(true)}
                                className="w-full py-2 rounded-md border border-amber-400 text-amber-700 text-xs font-bold bg-white hover:bg-amber-50 transition-colors"
                            >
                                Put on hold
                            </button>
                        )}
                        <button
                            onClick={() =>
                                navigate(`/patients/${c.id}?tab=actions`, {
                                    state: { workQueueSearch: location.search, patientName: c.name }
                                })
                            }
                            className="w-full py-2 rounded-md border border-gray-200 text-gray-500 text-xs font-medium bg-white hover:bg-gray-50 transition-colors"
                        >
                            Other actions
                        </button>
                    </div>
                </div>

                {/* Timeline */}
                <div className="mt-4">
                    <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.04em]">
                            Timeline
                        </span>
                        {timelineMode === 'activity' && timelineEvents.length > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                {timelineEvents.length}
                            </span>
                        )}
                        <div className="ml-auto">
                            <TimelineModeToggle mode={timelineMode} onChange={setTimelineMode} />
                        </div>
                    </div>

                    {timelineMode === 'journey' ? (
                        <JourneyView
                            segments={journeyQuery.data ?? []}
                            isLoading={journeyQuery.isLoading}
                            isError={journeyQuery.isError}
                        />
                    ) : (
                        <>
                            {timelineLoading && (
                                <div className="flex items-center justify-center py-4">
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                                </div>
                            )}

                            {!timelineLoading && timelineError && (
                                <div className="text-[11px] text-red-500 text-center py-3">
                                    Failed to load timeline
                                </div>
                            )}

                            {!timelineLoading && !timelineError && timelineEvents.length === 0 && (
                                <div className="text-[11px] text-gray-400 italic text-center py-3">
                                    No events yet
                                </div>
                            )}

                            {!timelineLoading && !timelineError && timelineEvents.length > 0 && (
                                <ul className="relative">
                                    {timelineEvents.map((event, idx) => (
                                        <TimelineRailEvent
                                            key={event.id}
                                            event={event}
                                            isLast={idx === timelineEvents.length - 1}
                                        />
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
